
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import axios from "axios";
import Select from "react-select";
import "./index.css";

import { BHAREE_BASE_URL_LOCAL } from "../../../../config/config.url";

const ReferralForm = () => {
  const initialFormData = {
    name: "",
    mobile: "",
    company: "",
    category: "",
    email: "",
    areaLocation: "",
    city: "",
    district: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [mobileError, setMobileError] = useState("");

  // Location dropdown states
  const [stateName, setStateName] = useState("");
  const [statesList, setStatesList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "mobile") {
      setMobileError("");
    }
  };

  // =====================================================
  // FETCH LOCATION DATA
  // =====================================================

  const getGeoDataDB = async (state, district, city) => {
    let apiURL = `${BHAREE_BASE_URL_LOCAL}/api/geoDataDB?countryCode=IN`;

    if (district) {
      apiURL += `&districtName=${encodeURIComponent(district)}`;
    }

    if (state) {
      apiURL += `&stateName=${encodeURIComponent(state)}`;
    }

    if (city) {
      apiURL += `&cityName=${encodeURIComponent(city)}`;
    }

    try {
      const res = await axios.get(apiURL, {
        headers: {
          "x-guest-token":
            localStorage.getItem("guest_jwt_token") || "",
        },
      });

      return res.data;
    } catch (error) {
      console.error("Referral location API error:", error);
      setLocationError(
        "Unable to load location options. Please try again."
      );
      return null;
    }
  };

  // Load states when the form opens
  useEffect(() => {
    let cancelled = false;

    const loadStates = async () => {
      setLocationLoading(true);
      setLocationError("");

      const res = await getGeoDataDB();

      if (cancelled) return;

      if (res?.success && Array.isArray(res.data)) {
        setStatesList(res.data);
      } else {
        setLocationError(
          "Unable to load states. Please refresh and try again."
        );
      }

      setLocationLoading(false);
    };

    loadStates();

    return () => {
      cancelled = true;
    };
  }, []);

  // =====================================================
  // STATE SELECTION
  // =====================================================

  const handleStateChange = async (selected) => {
    const state = selected?.value || "";

    setStateName(state);
    setFormData((prev) => ({
      ...prev,
      district: "",
      city: "",
      areaLocation: "",
    }));

    setDistrictList([]);
    setCityList([]);
    setAreaData([]);
    setLocationError("");

    if (!state) return;

    setLocationLoading(true);

    const res = await getGeoDataDB(state);

    if (res?.success && Array.isArray(res.data)) {
      setDistrictList(res.data);
    } else {
      setDistrictList([]);
      setLocationError("Unable to load districts.");
    }

    setLocationLoading(false);
  };

  // =====================================================
  // DISTRICT SELECTION
  // =====================================================

  const handleDistrictChange = async (selected) => {
    const district = selected?.value || "";

    setFormData((prev) => ({
      ...prev,
      district,
      city: "",
      areaLocation: "",
    }));

    setCityList([]);
    setAreaData([]);
    setLocationError("");

    if (!district || !stateName) return;

    setLocationLoading(true);

    const res = await getGeoDataDB(stateName, district);

    if (res?.success && Array.isArray(res.data)) {
      setCityList(res.data);
    } else {
      setCityList([]);
      setLocationError("Unable to load cities.");
    }

    setLocationLoading(false);
  };

  // =====================================================
  // CITY SELECTION
  // =====================================================

  const handleCityChange = async (selected) => {
    const city = selected?.value || "";

    setFormData((prev) => ({
      ...prev,
      city,
      areaLocation: "",
    }));

    setAreaData([]);
    setLocationError("");

    if (!city || !stateName || !formData.district) return;

    setLocationLoading(true);

    const res = await getGeoDataDB(
      stateName,
      formData.district,
      city
    );

    if (res?.success && Array.isArray(res.data)) {
      setAreaData(res.data);
    } else {
      setAreaData([]);
      setLocationError("Unable to load areas.");
    }

    setLocationLoading(false);
  };

  // =====================================================
  // AREA SELECTION
  // =====================================================

  const handleAreaChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      areaLocation: selected?.value || "",
    }));
  };

  // =====================================================
  // RESET LOCATION DROPDOWNS
  // =====================================================

  const resetLocationFields = () => {
    setStateName("");
    setDistrictList([]);
    setCityList([]);
    setAreaData([]);
    setLocationError("");
  };

  // =====================================================
  // SUBMIT REFERRAL
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    // Validate Indian mobile number
    if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      setMobileError(
        "Please enter a valid 10-digit mobile number."
      );
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("jwtToken");

      if (!token) {
        alert("Please login again.");
        return;
      }

      const response = await axios.post(
        `${BHAREE_BASE_URL_LOCAL}/referrals`,
        {
          name: formData.name,
          mobile: formData.mobile,
          company: formData.company,
          category: formData.category,
          email: formData.email,
          areaLocation: formData.areaLocation,
          city: formData.city,
          district: formData.district,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        alert(
          response?.data?.message ||
            "Referral submitted successfully!"
        );

        setFormData({ ...initialFormData });
        setMobileError("");
        resetLocationFields();
      } else {
        alert(
          response?.data?.message ||
            "Failed to submit referral."
        );
      }
    } catch (error) {
      console.error("Referral submit error:", error);

      if (error?.response?.status === 409) {
        setMobileError(
          error?.response?.data?.message ||
            "This mobile number has already been referred."
        );
        return;
      }

      alert(
        error?.response?.data?.message ||
          "Failed to submit referral. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const stateOptions = statesList.map((item) => ({
    value: item.state_name,
    label: item.state_name,
  }));

  const districtOptions = districtList.map((item) => ({
    value: item.district_name,
    label: item.district_name,
  }));

  const cityOptions = cityList.map((item) => ({
    value: item.city_name,
    label: item.city_name,
  }));

  const areaOptions = areaData
    .filter((item) => item.area)
    .map((item) => ({
      value: item.area,
      label: item.area,
    }));

  const selectedState = stateName
    ? { value: stateName, label: stateName }
    : null;

  const selectedDistrict = formData.district
    ? {
        value: formData.district,
        label: formData.district,
      }
    : null;

  const selectedCity = formData.city
    ? { value: formData.city, label: formData.city }
    : null;

  const selectedArea = formData.areaLocation
    ? {
        value: formData.areaLocation,
        label: formData.areaLocation,
      }
    : null;

  return (
    <div className="seller-referral-page">
      <Container fluid className="seller-referral-container">

        {/* HERO BANNER */}
        <div className="ref-hero-banner text-center">
          <p className="ref-hero-subtitle">
            YOUR BEVERAGES HUB – PARTNER REWARDS PROGRAM
          </p>

          <h1 className="ref-hero-title">
            REFER &amp; EARN REWARDS!
          </h1>

          <div className="ref-hero-badge">
            Introduce Businesses to Bharee &amp; Unlock Instant Rewards
          </div>

          <div className="ref-points-box">
            <h2>EARN 250 POINTS</h2>
          </div>

          <p className="ref-hero-footer-text">
            Per successful business referral on their first purchase!
          </p>
        </div>

        {/* HOW IT WORKS */}
        <div className="ref-section-block">
          <h3 className="ref-section-heading">
            HOW IT WORKS IN 3 SIMPLE STEPS
          </h3>

          <Row className="g-3">
            <Col md={4}>
              <div className="ref-step-card">
                <div className="ref-step-number">1</div>
                <h5>Submit Referral</h5>
                <p>
                  Log into your Bharee Portal and fill out the simple
                  Refer Form with your client's contact info.
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className="ref-step-card">
                <div className="ref-step-number">2</div>
                <h5>Onboarding</h5>
                <p>
                  Our sales team connects with the referred business,
                  verifies their profile, and assists their first order.
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className="ref-step-card">
                <div className="ref-step-number">3</div>
                <h5>Get Rewarded</h5>
                <p>
                  250 Points are credited to your Bharee account as soon
                  as the first order is delivered!
                </p>
              </div>
            </Col>
          </Row>
        </div>

        {/* WHO CAN YOU REFER */}
        <Row className="g-4 ref-section-block">
          <Col lg={6}>
            <div className="ref-info-box h-100">
              <h5 className="ref-box-title">
                🎯 Who Can You Refer?
              </h5>

              <p className="ref-box-desc">
                Refer any business or establishment  that
                purchases beverages or raw supplies in bulk:
              </p>

              <div className="ref-tags-container">
                <span className="ref-tag">Hotels &amp; Restaurants</span>
                <span className="ref-tag">Cafes &amp; Tea Outlets</span>
                <span className="ref-tag">Office Canteens</span>
                <span className="ref-tag">
                  Caterers &amp; Event Planners
                </span>
                <span className="ref-tag">
                  Wholesale Distributors
                </span>
                <span className="ref-tag">Fast Food Corners</span>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="ref-info-box h-100">
              <h5 className="ref-box-title">
                💼 Account Types &amp; Benefits
              </h5>

              <ul className="ref-benefits-list">
                <li>
                  <strong>Buyers </strong> Convert
                  referral points directly into cash payouts via UPI,
                  Bank Transfer.
                </li>
                <li>
                  <strong>Sellers </strong> Convert
                  referral points directly into cash payouts via UPI,
                  Bank Transfer.
                </li>
                
              </ul>
            </div>
          </Col>
        </Row>

        {/* REWARD REDEMPTION TABLE */}
        <div className="ref-section-block">
          <h3 className="ref-section-heading">
            REWARD REDEMPTION &amp; THRESHOLDS
          </h3>

          <div className="table-responsive ref-table-wrapper">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th>Participant Role</th>
                  <th>Minimum Threshold</th>
                  <th>Payout / Redemption Options</th>
                  <th>Processing SLA</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>Buyers </td>
                  <td>1,000 Points</td>
                  <td>Discount Voucher / Bank Transfer (NEFT) </td>
                  <td>3 Business Day</td>
                </tr>

                <tr>
                  <td>Sellers</td>
                  <td>1,000 Points</td>
                  <td>Discount Voucher / Bank Transfer (NEFT)</td>
                  <td>3 Business Day</td>
                </tr>

             
              </tbody>
            </Table>
          </div>
        </div>

        {/* REFERRAL FORM */}
        <Card className="seller-referral-card mt-4">
          <Card.Body>
            <div className="seller-referral-title">
              <h2>Refer Form</h2>
              <p>Refer &amp; Earn 250 Points</p>
            </div>

            <div className="seller-referral-line"></div>

           <Form id="referral-form" onSubmit={handleSubmit}>
              <Row>
                {/* NAME */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Name</Form.Label>

                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Enter referee full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>

                {/* COMPANY */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Company</Form.Label>

                    <Form.Control
                      type="text"
                      name="company"
                      placeholder="Enter Company address"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>

                {/* MOBILE */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Mobile</Form.Label>

                    <Form.Control
                      type="tel"
                      name="mobile"
                      maxLength={10}
                      placeholder="Enter mobile number"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      isInvalid={!!mobileError}
                    />

                    <Form.Control.Feedback type="invalid">
                      {mobileError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* EMAIL */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Email</Form.Label>

                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>

                {/* CATEGORY */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Category</Form.Label>

                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select Category</option>
                      <option value="Hotels & Restaurants">
                        Hotels &amp; Restaurants
                      </option>
                      <option value="Cafes & Tea Outlets">
                        Cafes &amp; Tea Outlets
                      </option>
                      <option value="Office Canteens">
                        Office Canteens
                      </option>
                      <option value="Caterers & Event Planners">
                        Caterers &amp; Event Planners
                      </option>
                      <option value="Fast Food Corners">
                        Fast Food Corners
                      </option>
                      <option value="Manufacturer">Manufacturer</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Wholesaler">Wholesaler</option>
                      <option value="Retailer">Retailer</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* STATE */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>State</Form.Label>

                    <Select
                      value={selectedState}
                      onChange={handleStateChange}
                      options={stateOptions}
                      placeholder={
                        locationLoading
                          ? "Loading locations..."
                          : "-- Select State --"
                      }
                      isSearchable
                      isClearable
                      isLoading={locationLoading}
                      isDisabled={loading || locationLoading}
                      className="react-select-container"
                      classNamePrefix="react-select-address"
                    />
                  </Form.Group>
                </Col>

                {/* DISTRICT */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>District</Form.Label>

                    <Select
                      value={selectedDistrict}
                      onChange={handleDistrictChange}
                      options={districtOptions}
                      placeholder="-- Select District --"
                      isSearchable
                      isClearable
                      isLoading={locationLoading && !!stateName}
                      isDisabled={
                        loading || locationLoading || !stateName
                      }
                      className="react-select-container"
                      classNamePrefix="react-select-address"
                    />
                  </Form.Group>
                </Col>

                {/* CITY */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>City</Form.Label>

                    <Select
                      value={selectedCity}
                      onChange={handleCityChange}
                      options={cityOptions}
                      placeholder="-- Select City --"
                      isSearchable
                      isClearable
                      isLoading={locationLoading && !!formData.district}
                      isDisabled={
                        loading ||
                        locationLoading ||
                        !formData.district
                      }
                      className="react-select-container"
                      classNamePrefix="react-select-address"
                    />
                  </Form.Group>
                </Col>

                {/* AREA / LOCATION */}
                <Col md={6} className="seller-referral-field">
                  <Form.Group>
                    <Form.Label>Area / Location</Form.Label>

                    <Select
                      value={selectedArea}
                      onChange={handleAreaChange}
                      options={areaOptions}
                      placeholder="-- Select Area --"
                      isSearchable
                      isClearable
                      isLoading={locationLoading && !!formData.city}
                      isDisabled={
                        loading ||
                        locationLoading ||
                        !formData.city
                      }
                      className="react-select-container"
                      classNamePrefix="react-select-address"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {locationError && (
                <p className="text-danger mt-2" role="alert">
                  {locationError}
                </p>
              )}

              {/* SUBMIT BUTTON */}
              <div className="seller-referral-submit">
                <button
                  type="submit"
                  className="seller-referral-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span className="seller-submit-icon">➤</span>
                      Submit Referral
                    </>
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ReferralForm;
